import { db } from "@/integrations/neon/db";
import { ProgramAIData } from "@/hooks/program/types";
import { toast } from "sonner";
import { fetchRealtimeRevisions } from "@/utils/realtimeRevisions";
import {
  areAllSocialPostsComplete,
  hasMagazineArticleContent,
  hasResearchArticleContent,
  isSocialBatchGenerating,
  isSocialPostReady,
} from "@/utils/programAiGenerationUi";

type SubscriptionCallback = (data: ProgramAIData) => void;
type ErrorCallback = (error: string) => void;
type StatusCallback = () => void;

interface SubscriptionOptions {
  onUpdate?: SubscriptionCallback;
  onError?: ErrorCallback;
  onArticleGenerated?: StatusCallback;
  onMagazineArticleGenerated?: StatusCallback;
  onSocialPostsGenerated?: StatusCallback;
  /** Any background generation flag dropped (success or failure). */
  onGenerationFinished?: StatusCallback;
}

const FAST_POLL_MS = 800;
const IDLE_POLL_MS = 3000;

/** Replaces Supabase postgres_changes with polling against Neon-backed `/api/database`. */
class ProgramRealTimeService {
  private intervals: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private callbacks: Map<string, SubscriptionOptions> = new Map();
  private lastUpdatedAt: Map<string, string | null> = new Map();
  /** Last seen Redis `rev:program_ai:{id}` when Redis is enabled. */
  private lastProgramAiRev: Map<string, number | undefined> = new Map();
  private currentView: Map<string, "analysis" | "magazine" | "social"> =
    new Map();
  private socialMediaState: Map<string, Partial<ProgramAIData>> = new Map();
  /** Previous AI row snapshot per program (for transition-based toasts). */
  private prevAiSnapshot: Map<string, Partial<ProgramAIData>> = new Map();
  private pollingMode: Map<string, "fast" | "idle"> = new Map();

  constructor() {}

  setCurrentView(
    programId: string,
    view: "analysis" | "magazine" | "social"
  ) {
    this.currentView.set(programId, view);
  }

  subscribeToProgram(
    programId: string,
    options: SubscriptionOptions
  ): () => void {
    if (!programId) {
      console.error("Cannot subscribe without a program ID");
      return () => {};
    }

    this.callbacks.set(programId, options);

    if (this.intervals.has(programId)) {
      return () => this.unsubscribe(programId);
    }

    const tick = async () => {
      try {
        const revs = await fetchRealtimeRevisions({ programId });
        if (!this.callbacks.has(programId)) return;
        if (revs.redisConfigured) {
          const rev = revs.programAiRev ?? 0;
          const lastRev = this.lastProgramAiRev.get(programId);
          const inFastMode = this.pollingMode.get(programId) === "fast";
          // When Redis rev is unchanged we normally skip the DB round-trip. During active
          // generation (`fast` polling), Postgres can advance while `INCR` failed silently
          // (see redisRevision.ts), which would freeze UI on stale `generating_*` flags.
          if (
            lastRev !== undefined &&
            rev === lastRev &&
            !inFastMode
          ) {
            return;
          }
          this.lastProgramAiRev.set(programId, rev);
        } else {
          this.lastProgramAiRev.delete(programId);
        }

        const { data, error } = await db
          .from("program_ai_data")
          .select("*")
          .eq("program_id", programId)
          .maybeSingle();

        if (!this.callbacks.has(programId)) return;

        if (error) {
          console.error("Program AI poll error:", error);
          options.onError?.(error.message || "Poll failed");
          return;
        }
        if (!data) return;

        const row = data as ProgramAIData;
        const prevAt = this.lastUpdatedAt.get(programId);
        if (!revs.redisConfigured) {
          if (row.updated_at && row.updated_at === prevAt) {
            return;
          }
        }
        if (row.updated_at) {
          this.lastUpdatedAt.set(programId, row.updated_at);
        }

        this.deliverAiUpdate(programId, row, options);
        const isGenerating =
          row.generating_analysis === true ||
          row.generating_summary === true ||
          row.generating_research_article === true ||
          row.generating_magazine_article === true ||
          row.generating_social_media_posts === true;
        this.pollingMode.set(programId, isGenerating ? "fast" : "idle");
      } catch (e) {
        console.error("Program AI poll exception:", e);
        if (this.callbacks.has(programId)) {
          options.onError?.("Failed to refresh program data");
        }
      } finally {
        if (this.callbacks.has(programId)) {
          const mode = this.pollingMode.get(programId) ?? "idle";
          const delay = mode === "fast" ? FAST_POLL_MS : IDLE_POLL_MS;
          const timeoutId = setTimeout(() => void tick(), delay);
          this.intervals.set(programId, timeoutId);
        }
      }
    };

    this.pollingMode.set(programId, "fast");
    void tick();

    return () => this.unsubscribe(programId);
  }

  private deliverAiUpdate(
    programId: string,
    updatedData: ProgramAIData,
    options: SubscriptionOptions
  ) {
    const currentView = this.currentView.get(programId) || "analysis";
    const prev = this.prevAiSnapshot.get(programId) ?? {};

    const isArticleComplete = hasResearchArticleContent(updatedData);
    const isMagazineComplete = hasMagazineArticleContent(updatedData);

    const socialPostsGenerating = isSocialBatchGenerating(updatedData);
    const socialPostsComplete = areAllSocialPostsComplete(updatedData);
    const prevPl = prev.generating_social_platform ?? null;
    const curPl = updatedData.generating_social_platform ?? null;

    const previousLinkedInState = this.getPreviousSocialMediaState(
      programId,
      "ai_linkedin_post"
    );
    const previousInstagramState = this.getPreviousSocialMediaState(
      programId,
      "ai_instagram_post"
    );
    const previousXState = this.getPreviousSocialMediaState(
      programId,
      "ai_x_post"
    );
    const previousThreadsState = this.getPreviousSocialMediaState(
      programId,
      "ai_threads_post"
    );
    const previousFacebookState = this.getPreviousSocialMediaState(
      programId,
      "ai_facebook_post"
    );

    const isLinkedInNewlyGenerated =
      (prevPl === "linkedin" &&
        curPl !== "linkedin" &&
        isSocialPostReady(updatedData.ai_linkedin_post)) ||
      (previousLinkedInState === "Generating..." &&
        isSocialPostReady(updatedData.ai_linkedin_post));

    const isInstagramNewlyGenerated =
      (prevPl === "instagram" &&
        curPl !== "instagram" &&
        isSocialPostReady(updatedData.ai_instagram_post)) ||
      (previousInstagramState === "Generating..." &&
        isSocialPostReady(updatedData.ai_instagram_post));

    const isXNewlyGenerated =
      (prevPl === "x" &&
        curPl !== "x" &&
        isSocialPostReady(updatedData.ai_x_post)) ||
      (previousXState === "Generating..." &&
        isSocialPostReady(updatedData.ai_x_post));

    const isThreadsNewlyGenerated =
      (prevPl === "threads" &&
        curPl !== "threads" &&
        isSocialPostReady(updatedData.ai_threads_post)) ||
      (previousThreadsState === "Generating..." &&
        isSocialPostReady(updatedData.ai_threads_post));

    const isFacebookNewlyGenerated =
      (prevPl === "facebook" &&
        curPl !== "facebook" &&
        isSocialPostReady(updatedData.ai_facebook_post)) ||
      (previousFacebookState === "Generating..." &&
        isSocialPostReady(updatedData.ai_facebook_post));

    this.updateSocialMediaState(programId, updatedData);

    const articleFinished =
      (prev.generating_research_article === true &&
        !updatedData.generating_research_article &&
        isArticleComplete) ||
      (prev.ai_article === "Regenerating..." && isArticleComplete);
    if (articleFinished && options.onArticleGenerated) {
      options.onArticleGenerated();
      if (currentView === "analysis") {
        toast.success("Article generated successfully");
      }
    }

    const magazineFinished =
      (prev.generating_magazine_article === true &&
        !updatedData.generating_magazine_article &&
        isMagazineComplete) ||
      (prev.ai_magazine_article === "Regenerating..." && isMagazineComplete);
    if (magazineFinished && options.onMagazineArticleGenerated) {
      options.onMagazineArticleGenerated();
      if (currentView === "magazine") {
        toast.success("Magazine article generated successfully");
      }
    }

    if (currentView === "social") {
      if (isLinkedInNewlyGenerated) toast.success("LinkedIn post generated");
      if (isInstagramNewlyGenerated) toast.success("Instagram post generated");
      if (isXNewlyGenerated) toast.success("X (Twitter) post generated");
      if (isThreadsNewlyGenerated) toast.success("Threads post generated");
      if (isFacebookNewlyGenerated) toast.success("Facebook post generated");
    }

    const wasSocialGenerating =
      prev.generating_social_media_posts === true ||
      prev.ai_linkedin_post === "Generating..." ||
      prev.ai_instagram_post === "Generating..." ||
      prev.ai_x_post === "Generating..." ||
      prev.ai_threads_post === "Generating..." ||
      prev.ai_facebook_post === "Generating...";

    if (
      wasSocialGenerating &&
      socialPostsComplete &&
      !socialPostsGenerating &&
      options.onSocialPostsGenerated
    ) {
      options.onSocialPostsGenerated();
    }

    // Background jobs clear their flag on failure too. These are the only failure signals the
    // UI gets, since the start request has already returned.
    const summaryStopped =
      prev.generating_summary === true && !updatedData.generating_summary;
    const researchStopped =
      prev.generating_research_article === true &&
      !updatedData.generating_research_article;
    const magazineStopped =
      prev.generating_magazine_article === true &&
      !updatedData.generating_magazine_article;
    const socialStopped =
      prev.generating_social_media_posts === true &&
      !updatedData.generating_social_media_posts;
    if (summaryStopped && updatedData.summary_progress === "failed") {
      toast.error("Analysis generation failed. Try again.");
    }
    // A manual reset writes null; a failed job leaves "" or a partial draft.
    if (researchStopped && !isArticleComplete && updatedData.ai_article !== null) {
      toast.error("Research article generation failed. Try again.");
    }
    if (
      magazineStopped &&
      !isMagazineComplete &&
      updatedData.ai_magazine_article !== null
    ) {
      toast.error("Magazine article generation failed. Try again.");
    }
    if (socialStopped && !socialPostsComplete) {
      toast.error("Social post generation failed. Try again.");
    }
    if (summaryStopped || researchStopped || magazineStopped || socialStopped) {
      options.onGenerationFinished?.();
    }

    options.onUpdate?.(updatedData);

    this.prevAiSnapshot.set(programId, {
      ai_article: updatedData.ai_article,
      ai_magazine_article: updatedData.ai_magazine_article,
      ai_linkedin_post: updatedData.ai_linkedin_post,
      ai_instagram_post: updatedData.ai_instagram_post,
      ai_x_post: updatedData.ai_x_post,
      ai_threads_post: updatedData.ai_threads_post,
      ai_facebook_post: updatedData.ai_facebook_post,
      generating_summary: updatedData.generating_summary,
      generating_research_article: updatedData.generating_research_article,
      generating_magazine_article: updatedData.generating_magazine_article,
      generating_social_media_posts: updatedData.generating_social_media_posts,
      generating_social_platform: updatedData.generating_social_platform,
    });
  }

  private updateSocialMediaState(programId: string, data: ProgramAIData) {
    const previousState = this.socialMediaState.get(programId) || {};

    this.socialMediaState.set(programId, {
      ai_linkedin_post: data.ai_linkedin_post,
      ai_instagram_post: data.ai_instagram_post,
      ai_x_post: data.ai_x_post,
      ai_threads_post: data.ai_threads_post,
      ai_facebook_post: data.ai_facebook_post,
    });
  }

  private getPreviousSocialMediaState(
    programId: string,
    field: keyof ProgramAIData
  ): string | null {
    const state = this.socialMediaState.get(programId);
    return state ? (state[field] as string | null) : null;
  }

  unsubscribe(programId: string): void {
    if (!programId) return;
    /** Stop in-flight ticks from delivering or rescheduling after navigation away. */
    this.callbacks.delete(programId);
    const id = this.intervals.get(programId);
    if (id !== undefined) {
      clearTimeout(id);
      this.intervals.delete(programId);
    }
    this.lastUpdatedAt.delete(programId);
    this.lastProgramAiRev.delete(programId);
    this.prevAiSnapshot.delete(programId);
    this.pollingMode.delete(programId);
  }

  unsubscribeAll(): void {
    this.intervals.forEach((id) => clearTimeout(id));
    this.intervals.clear();
    this.callbacks.clear();
    this.lastUpdatedAt.clear();
    this.lastProgramAiRev.clear();
    this.socialMediaState.clear();
    this.prevAiSnapshot.clear();
    this.pollingMode.clear();
  }
}

export const programRealTimeService = new ProgramRealTimeService();
